import { useState } from 'react'
import toast from 'react-hot-toast'
import {
  useLaunchSalvage,
  useSalvageEstimate,
  useMyFleet,
  useMyMissions,
  ongoingMissionTo,
  type MapIslandDto,
} from '@/api/hooks'
import { groupShipsByType, missionFleetCap } from '@/lib/fleetGroups'
import { ResourceTally } from '@/components/game/ui'
import { RumIcon, ReefIcon } from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import { ShipPickerList } from '@/components/game/map/launcherBits'
import { combatMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { TripDuration, ConvoyPace } from '@/components/game/map/tripDuration'

/**
 * Salvage launcher for a récif (ex-"tempête") — a non-combat gamble. You send a
 * fleet to scavenge a wreck-strewn reef: a flat best-case reward (gold/wood/rhum)
 * per run, paid for in rhum, against a per-ship-type SINK risk (the reef can claim
 * a coque). No threat / danger / combat dice — just the bet. A récif has a finite
 * number of salvage charges (`salvageRemaining`); once depleted, no more runs.
 * All numbers (duration, rhum cost, sink rates, reward) come from the backend
 * estimate — the front only displays them.
 */
export function SalvageLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchSalvage()

  // One salvage run at a time per reef (the backend rejects a second, 409).
  const ongoing = ongoingMissionTo(missionsData?.missions ?? [], island.id, 'recif_salvage')
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  const charges = island.salvageRemaining ?? 0
  const depleted = charges <= 0

  // Don't preview a depleted récif: the CTA is disabled regardless, so skip the call.
  const estimate = useSalvageEstimate(selectedIds, island.id, selectedIds.length > 0 && !depleted)

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  // The reef's own facts — its name and its charge stock — are public and hold
  // whatever the viewer's fleet is doing. Rendered in BOTH branches below: the
  // count used to sit past the "no docked ship" early return, so a captain with
  // every hull at sea lost the one number the next run is planned on.
  const header = (
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs uppercase tracking-wider flex items-center gap-1.5">
        <ReefIcon className="w-4 h-4 text-ocean-300" />
        <span className="text-ocean-300">Récif</span>
      </p>
      <p className={`text-xs whitespace-nowrap tabular-nums ${depleted ? 'text-rum-500 font-semibold' : 'text-ocean-300'}`}>
        Charges restantes : <strong className={depleted ? 'text-rum-500' : 'text-sand-300'}>{charges}</strong>
      </p>
    </div>
  )

  const depletedNotice = depleted && (
    <p className="text-xs text-rum-500 italic border-l-2 border-rum-500 pl-2">
      Récif épuisé, plus rien à récupérer ici.
    </p>
  )

  if (dockedShips.length === 0) {
    return (
      <div className="bg-ocean-900/45 rounded-lg p-3 space-y-3 border border-sand-500/15">
        {header}
        <p className="text-xs text-ocean-300 italic">
          Aucun navire à quai pour une récupération.
        </p>
        {depletedNotice}
      </div>
    )
  }

  const reward = estimate.data?.reward
  // Distinct ship types in the selection → their per-type sink probability. Each
  // line reads as "the chance THIS hull never comes back" — the heart of the bet.
  const sinkRates = estimate.data?.sinkRates ?? {}
  const riskGroups = groupShipsByType(selected)

  const rhumAvailable = fleet?.resources?.rum ?? 0
  // Authoritative: the backend's rhumNeeded, never a client recompute.
  const notEnoughRhum = !!estimate.data && estimate.data.rhumNeeded > rhumAvailable

  return (
    <div className="bg-ocean-900/45 rounded-lg p-3 space-y-3 border border-sand-500/15">
      {header}

      <p className="text-[11px] text-ocean-300 leading-snug">
        Fouiller les épaves du récif rapporte un butin, mais la mer est traître : chaque navire
        engagé peut y rester. C'est un pari.
      </p>

      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={combatMeta}
      />

      {selected.length > 0 && estimate.data !== undefined && reward !== undefined && (
        <div className="text-xs space-y-2 bg-ocean-900/50 rounded p-2 border border-sand-500/15">
          <div className="flex justify-between items-center text-ocean-200">
            <span className="text-[11px] uppercase tracking-wider text-ocean-300">Butin (au mieux)</span>
            <ResourceTally
              gold={reward.gold || undefined}
              wood={reward.wood || undefined}
              rhum={reward.rhum || undefined}
              className="text-sand-300 font-semibold"
            />
          </div>
          <div className="flex justify-between items-center text-ocean-300">
            <span>Coût</span>
            <span className={`tabular-nums inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>
              {estimate.data.rhumNeeded.toLocaleString('fr-FR')} <RumIcon className="w-3.5 h-3.5" />
              {notEnoughRhum && <span className="ml-1">(banque : {rhumAvailable.toLocaleString('fr-FR')})</span>}
            </span>
          </div>
          <div className="flex justify-between items-center text-ocean-300">
            <span>Trajet</span>
            <TripDuration seconds={estimate.data.durationSeconds} />
          </div>
          <ConvoyPace ships={selected} tripSeconds={estimate.data.durationSeconds} />
          <div className="border-t border-sand-500/15 pt-1.5 mt-1.5 space-y-1">
            <p className="text-[11px] uppercase tracking-wider text-ocean-300">Risque de perdre chaque navire</p>
            {riskGroups.map((g) => (
              <div key={g.type} className="flex justify-between items-center text-ocean-200">
                <span>{g.label}</span>
                <span className="font-display tabular-nums text-rum-500">
                  {Math.round((sinkRates[g.type] ?? 0) * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {depletedNotice}

      <p className="text-ocean-300 text-[11px]">Tout navire perdu l'est définitivement.</p>

      {ongoing && (
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <ReefIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>
            Une récupération est déjà en cours sur ce récif
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
      )}

      <button
        onClick={() => {
          if (selected.length === 0) return
          const shipIds = selected.map((s) => s.id)
          launch.mutate(
            { shipIds, destinationIslandId: island.id },
            {
              onSuccess: () =>
                toast.success(`${selected.length} navire(s) en route vers ${island.name}.`),
              onError: (err) => toast.error(err.message || 'Récupération impossible'),
            },
          )
          setSelectedIds([])
        }}
        disabled={launch.isPending || selected.length === 0 || depleted || notEnoughRhum || ongoing != null}
        title={ongoing ? 'Une récupération est déjà en cours sur ce récif.' : undefined}
        className="w-full text-sm px-3 py-1.5 rounded-md bg-rum-500 text-ocean-50 font-semibold hover:bg-rum-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
      >
        {ongoing ? (
          'Récupération en cours'
        ) : launch.isPending ? (
          'Lancement…'
        ) : depleted ? (
          'Récif épuisé'
        ) : notEnoughRhum ? (
          'Rhum insuffisant'
        ) : (
          <><ReefIcon className="w-4 h-4" /> Lancer la récupération ({selected.length})</>
        )}
      </button>
    </div>
  )
}
